import type { SubscriptionTier } from "@/types/projects";
import { getDisplayLabelFromAssetRef } from "@/features/image-generation/assetRefs";
import { buildArtstyleAnchorCitations } from "@/features/image-generation/promptBuilders";
import type {
  GenerateImagePreviewResult,
  ImageContextCacheStatus,
  ImageGenerationAccessState,
  ImageGenerationDraftSession,
  ImageGenerationErrorBody,
  ImageGenerationTarget,
  ImageSourceCitation,
  ImageSourceKind,
  ProjectImageSettingsView,
  ProjectArtstyleReferenceView,
} from "@/features/image-generation/imageGeneration.types";

const DEFAULT_CREDIT_COST = 8;

function coerceImageCacheStatus(value: unknown): ImageContextCacheStatus {
  if (value === "hit") {
    return "hit";
  }

  if (value === "miss" || value === "invalidated") {
    return "miss";
  }

  return "not-applicable";
}

function getTierRank(tier: SubscriptionTier | null | undefined) {
  switch (tier ?? "free") {
    case "author":
      return 2;
    case "crafter":
      return 1;
    default:
      return 0;
  }
}

function getTargetLabel(sourceKind: ImageSourceKind): ImageGenerationTarget {
  switch (sourceKind) {
    case "chapter":
      return "Chapter illustration";
    case "side_story":
      return "Side story illustration";
    case "timeline_event":
      return "Timeline event illustration";
    case "lore_entry":
      return "Lore image";
    case "entity_card":
      return "Entity image";
  }
}

export function mapProjectArtstyleReference(
  input: {
    path: string;
    previewUrl?: string | null;
    styleKeywords?: string[] | null;
  },
): ProjectArtstyleReferenceView {
  return {
    fileName: getDisplayLabelFromAssetRef(input.path) || "Artstyle reference",
    id: input.path,
    path: input.path,
    previewUrl: input.previewUrl ?? null,
    styleKeywords: input.styleKeywords?.filter(Boolean) ?? [],
  };
}

export function mapImageCitation(input: {
  canonStatusLabel?: string | null;
  reason: string;
  sourceKind: ImageSourceCitation["sourceKind"];
  sourceLabel: string;
  sourceRecordId?: string | null;
}): ImageSourceCitation {
  return {
    canonStatusLabel: input.canonStatusLabel ?? null,
    reason: input.reason,
    sourceKind: input.sourceKind,
    sourceLabel: input.sourceLabel,
    sourceRecordId: input.sourceRecordId ?? null,
  };
}

export function createArtstyleRequiredAccessState(
  creditCost = DEFAULT_CREDIT_COST,
): ImageGenerationAccessState {
  return {
    cacheStatus: "not-applicable",
    code: "artstyle-required",
    creditCost,
    creditsSafe: true,
    creditsRemaining: null,
    message: "ARTSTYLE_REFERENCES_REQUIRED",
    recommendedTier: null,
    resetAt: null,
    retryAfterSeconds: null,
    tier: null,
    truncated: false,
  };
}

export function createImageAccessState(input: {
  cacheStatus?: ImageContextCacheStatus | "invalidated" | "none" | null;
  code?: ImageGenerationAccessState["code"];
  creditCost?: number | null;
  creditsSafe?: boolean;
  creditsRemaining?: number | null;
  message?: string | null;
  recommendedTier?: SubscriptionTier | null;
  resetAt?: string | null;
  retryAfterSeconds?: number | null;
  stage?: string | null;
  tier?: SubscriptionTier | null;
  truncated?: boolean | null;
}): ImageGenerationAccessState {
  return {
    cacheStatus: coerceImageCacheStatus(input.cacheStatus),
    code: input.code ?? "available",
    creditCost: input.creditCost ?? DEFAULT_CREDIT_COST,
    creditsSafe: input.creditsSafe ?? input.code !== "available",
    creditsRemaining: input.creditsRemaining ?? null,
    message: input.message ?? null,
    recommendedTier: input.recommendedTier ?? null,
    resetAt: input.resetAt ?? null,
    retryAfterSeconds: input.retryAfterSeconds ?? null,
    stage:
      typeof input.stage === "string" && input.stage.trim().length > 0
        ? input.stage
        : null,
    tier: input.tier ?? null,
    truncated: input.truncated === true,
  };
}

export function buildInitialImageAccessState(input: {
  creditCost?: number | null;
  creditsRemaining?: number | null;
  hasArtDirection: boolean;
  minimumTier?: SubscriptionTier;
  tier?: SubscriptionTier | null;
}): ImageGenerationAccessState {
  const creditCost = input.creditCost ?? DEFAULT_CREDIT_COST;
  const tier = input.tier ?? "free";
  const minimumTier = input.minimumTier ?? "crafter";
  const creditsRemaining = input.creditsRemaining ?? null;

  if (!input.hasArtDirection) {
    return {
      ...createArtstyleRequiredAccessState(creditCost),
      tier,
    };
  }

  if (getTierRank(tier) < getTierRank(minimumTier)) {
    return createImageAccessState({
      code: "tier-locked",
      creditCost,
      creditsRemaining,
      message: "TIER_LOCKED",
      recommendedTier: minimumTier,
      tier,
    });
  }

  if ((creditsRemaining ?? 0) < creditCost) {
    return createImageAccessState({
      code: "insufficient-credits",
      creditCost,
      creditsRemaining,
      message: "INSUFFICIENT_CREDITS",
      tier,
    });
  }

  return createImageAccessState({
    code: "available",
    creditCost,
    creditsRemaining,
    tier,
  });
}

export function mapImageGenerationErrorBody(
  body: ImageGenerationErrorBody | null | undefined,
): ImageGenerationAccessState {
  const code = body?.code ?? "IMAGE_GENERATION_FAILED";
  const creditsRemaining = body?.creditsRemaining ?? body?.currentBalance ?? null;

  switch (code) {
    case "TIER_LOCKED":
      return createImageAccessState({
        code: "tier-locked",
        creditCost: body?.creditCost,
        creditsRemaining,
        message: body?.message ?? code,
        recommendedTier: body?.recommendedTier ?? "crafter",
        tier: body?.tier ?? null,
      });
    case "INSUFFICIENT_CREDITS":
      return createImageAccessState({
        code: "insufficient-credits",
        creditCost: body?.creditCost,
        creditsSafe: body?.creditsSafe ?? false,
        creditsRemaining,
        cacheStatus: body?.cacheStatus ?? null,
        message: body?.message ?? code,
        tier: body?.tier ?? null,
        stage: body?.stage ?? null,
        truncated: body?.truncated ?? false,
      });
    case "AI_RATE_LIMIT_REACHED":
      return createImageAccessState({
        code: "rate-limit-reached",
        creditCost: body?.creditCost,
        creditsSafe: body?.creditsSafe ?? true,
        creditsRemaining,
        cacheStatus: body?.cacheStatus ?? null,
        message: body?.message ?? code,
        resetAt: body?.resetAt ?? null,
        retryAfterSeconds: body?.retryAfterSeconds ?? null,
        stage: body?.stage ?? null,
        tier: body?.tier ?? null,
        truncated: body?.truncated ?? false,
      });
    case "AI_BUDGET_RESTRICTED":
    case "AI_IMAGE_BUDGET_RESTRICTED":
      return createImageAccessState({
        code: "budget-restricted",
        creditCost: body?.creditCost,
        creditsSafe: body?.creditsSafe ?? true,
        creditsRemaining,
        cacheStatus: body?.cacheStatus ?? null,
        message: body?.message ?? code,
        resetAt: body?.resetAt ?? null,
        retryAfterSeconds: body?.retryAfterSeconds ?? null,
        stage: body?.stage ?? null,
        tier: body?.tier ?? null,
        truncated: body?.truncated ?? false,
      });
    case "AI_SERVICE_BUSY":
      return createImageAccessState({
        code: "service-busy",
        creditCost: body?.creditCost,
        creditsSafe: body?.creditsSafe ?? true,
        creditsRemaining,
        cacheStatus: body?.cacheStatus ?? null,
        message: body?.message ?? code,
        retryAfterSeconds: body?.retryAfterSeconds ?? null,
        stage: body?.stage ?? null,
        tier: body?.tier ?? null,
        truncated: body?.truncated ?? false,
      });
    case "ARTSTYLE_REFERENCES_REQUIRED":
      return {
        ...createArtstyleRequiredAccessState(body?.creditCost ?? DEFAULT_CREDIT_COST),
        cacheStatus: coerceImageCacheStatus(body?.cacheStatus),
        creditsSafe: body?.creditsSafe ?? true,
        resetAt: body?.resetAt ?? null,
        retryAfterSeconds: body?.retryAfterSeconds ?? null,
        stage: body?.stage ?? null,
        tier: body?.tier ?? null,
        truncated: body?.truncated === true,
      };
    default:
      return createImageAccessState({
        code: "available",
        creditCost: body?.creditCost,
        creditsSafe: body?.creditsSafe ?? false,
        creditsRemaining,
        cacheStatus: body?.cacheStatus ?? null,
        message: body?.message ?? code,
        resetAt: body?.resetAt ?? null,
        retryAfterSeconds: body?.retryAfterSeconds ?? null,
        stage: body?.stage ?? null,
        tier: body?.tier ?? null,
        truncated: body?.truncated ?? false,
      });
  }
}

export function buildImageDraftSessionFromSource(input: {
  creditCost?: number;
  creditsRemaining?: number | null;
  existingImagePath?: string | null;
  minimumTier?: SubscriptionTier;
  projectId: string;
  projectImageSettings: Pick<
    ProjectImageSettingsView,
    "artstyleReferences" | "hasArtDirection"
  >;
  prompt: string;
  sourceCitations?: ImageSourceCitation[];
  sourceId: string;
  sourceKind: ImageSourceKind;
  sourceTitle: string;
  tier?: SubscriptionTier | null;
}) {
  const accessState = buildInitialImageAccessState({
    creditCost: input.creditCost,
    creditsRemaining: input.creditsRemaining,
    hasArtDirection: input.projectImageSettings.hasArtDirection,
    minimumTier: input.minimumTier,
    tier: input.tier,
  });

  return buildImageDraftSession({
    accessState,
    artstyleReferences: input.projectImageSettings.artstyleReferences,
    citations: [
      ...(input.sourceCitations ?? []),
      ...buildArtstyleAnchorCitations(input.projectImageSettings.artstyleReferences),
    ],
    creditCost: accessState.creditCost,
    creditsRemaining: accessState.creditsRemaining,
    editedPrompt: input.prompt,
    existingImagePath: input.existingImagePath ?? null,
    projectId: input.projectId,
    sourceId: input.sourceId,
    sourceKind: input.sourceKind,
    sourceTitle: input.sourceTitle,
  });
}

export function buildImageDraftSession(input: {
  accessState?: ImageGenerationAccessState;
  artstyleReferences: ProjectArtstyleReferenceView[];
  citations?: ImageSourceCitation[];
  creditCost?: number;
  creditsRemaining?: number | null;
  editedPrompt: string;
  existingImagePath?: string | null;
  modelName?: string;
  previewDataUrl?: string | null;
  projectId: string;
  providerLabel?: string;
  requestStatus?: ImageGenerationDraftSession["requestStatus"];
  sourceId: string;
  sourceKind: ImageSourceKind;
  sourceTitle: string;
  tempAssetRef?: string | null;
}): ImageGenerationDraftSession {
  return {
    accessState:
      input.accessState
      ?? createImageAccessState({
        creditCost: input.creditCost,
        creditsRemaining: input.creditsRemaining,
      }),
    artstyleReferences: input.artstyleReferences,
    citations: input.citations ?? [],
    creditCost: input.creditCost ?? DEFAULT_CREDIT_COST,
    creditsRemaining: input.creditsRemaining ?? null,
    editedPrompt: input.editedPrompt,
    errorMessage: null,
    existingImagePath: input.existingImagePath ?? null,
    modelName: input.modelName ?? "gpt-image-1",
    previewDataUrl: input.previewDataUrl ?? null,
    projectId: input.projectId,
    providerLabel: input.providerLabel ?? "OpenAI",
    requestStatus: input.requestStatus ?? "idle",
    sourceId: input.sourceId,
    sourceKind: input.sourceKind,
    sourceTitle: input.sourceTitle,
    targetLabel: getTargetLabel(input.sourceKind),
    tempAssetRef: input.tempAssetRef ?? null,
  };
}

export function applyPreviewResultToDraftSession(
  session: ImageGenerationDraftSession,
  result: GenerateImagePreviewResult,
): ImageGenerationDraftSession {
  return {
    ...session,
    accessState: result.accessState,
    citations: result.citations,
    creditCost: result.creditCost,
    creditsRemaining: result.creditsRemaining,
    errorMessage: null,
    previewDataUrl: result.previewUrl,
    requestStatus: "ready",
    tempAssetRef: result.tempAssetRef,
  };
}
